import {
  type DatabaseAdapter,
  type FocusedItem,
  type SelectionIntent,
  type SelectionsReader,
} from '@databases/selections-api-types';

import { type SelectionsApi } from '../../selections-api';

export interface SelectionsApiReaderOptions {
  selectionsApi: SelectionsApi;
  adapter: DatabaseAdapter;
}

export class SelectionsApiReader implements SelectionsReader {
  #api: SelectionsApi;
  #adapter: DatabaseAdapter;

  constructor(options: SelectionsApiReaderOptions) {
    this.#api = options.selectionsApi;
    this.#adapter = options.adapter;
  }

  hasSelection(): boolean {
    return this.#api.hasSelection();
  }

  isValueSelected(entryId: string, fieldId: string): boolean {
    return this.#api.isValueSelected(entryId, fieldId);
  }

  getSelectionMode() {
    return this.#api.getSelectionMode();
  }

  getSelectedValues(): { entryId: string; fieldId: string }[] {
    const resolved = this.#api.getResolvedSelection();

    if (resolved.mode === 'cell') {
      return resolved.values.map((v) => ({ entryId: v.entryId, fieldId: v.fieldId }));
    }

    return [];
  }

  getSelectedFieldIds(): string[] {
    const resolved = this.#api.getResolvedSelection();

    if (resolved.mode === 'column') {
      return resolved.fields.map((f) => f.fieldId);
    }

    return [];
  }

  getFieldIdsFromSelection(): string[] {
    const resolved = this.#api.getResolvedSelection();

    if (resolved.mode === 'column') {
      return resolved.fields.map((f) => f.fieldId);
    }

    if (resolved.mode === 'cell') {
      return [...new Set(resolved.values.map((v) => v.fieldId))];
    }

    return [];
  }

  getSelectedEntryIds(): string[] {
    const resolved = this.#api.getResolvedSelection();

    if (resolved.mode === 'row') {
      return resolved.entries.map((e) => e.entryId);
    }

    return [];
  }

  getEntryIdsFromSelection(): string[] {
    const resolved = this.#api.getResolvedSelection();

    if (resolved.mode === 'row') {
      return resolved.entries.map((e) => e.entryId);
    }

    if (resolved.mode === 'cell') {
      return [...new Set(resolved.values.map((v) => v.entryId))];
    }

    return [];
  }

  getSelectionCount(): number {
    return this.#api.getSelectionCount();
  }

  isFieldSelected(fieldId: string): boolean {
    const resolved = this.#api.getResolvedSelection();

    return (
      resolved.mode === 'column' && resolved.fields.some((f) => f.fieldId === fieldId)
    );
  }

  isFieldInSelection(fieldId: string): boolean {
    return this.getFieldIdsFromSelection().includes(fieldId);
  }

  isEntrySelected(entryId: string): boolean {
    const resolved = this.#api.getResolvedSelection();

    return resolved.mode === 'row' && resolved.entries.some((e) => e.entryId === entryId);
  }

  isEntryInSelection(entryId: string): boolean {
    return this.getEntryIdsFromSelection().includes(entryId);
  }

  hasFieldBeforeSelection(): boolean {
    const mode = this.#api.getSelectionMode();

    return mode === 'row' ? false : this.#api.canMoveLeft();
  }

  hasFieldAfterSelection(): boolean {
    const mode = this.#api.getSelectionMode();

    return mode === 'row' ? false : this.#api.canMoveRight();
  }

  hasEntryBeforeSelection(): boolean {
    const mode = this.#api.getSelectionMode();

    return mode === 'column' ? false : this.#api.canMoveUp();
  }

  hasEntryAfterSelection(): boolean {
    const mode = this.#api.getSelectionMode();

    return mode === 'column' ? false : this.#api.canMoveDown();
  }

  isDeleteColumnsDisabled(): boolean {
    const totalFieldCount = this.#adapter.getVisibleFieldIds().length;

    if (totalFieldCount === 1) {
      return true;
    }

    const mode = this.#api.getSelectionMode();
    const fieldIds = this.getFieldIdsFromSelection();

    return (
      (mode === 'column' && this.getSelectionCount() === totalFieldCount) ||
      (mode === 'cell' && fieldIds.length === totalFieldCount) ||
      mode === 'row'
    );
  }

  getSelectionIntent(): SelectionIntent | null {
    return null;
  }

  getFocusedItem(): FocusedItem | undefined {
    const focusedCell = this.#api.getFocusedCell();

    if (!focusedCell) {
      return undefined;
    }

    const mode = this.#api.getSelectionMode();
    const fieldIds = this.#adapter.getVisibleFieldIds();
    const entryIds = this.#adapter.getVisibleEntryIds();
    const fieldId = fieldIds[focusedCell.col.index];
    const entryId = entryIds[focusedCell.row.index];

    if (mode === 'column' && fieldId) {
      return { type: 'field', fieldId };
    }

    if (mode === 'row' && entryId) {
      return { type: 'entry', entryId };
    }

    if (mode === 'cell' && fieldId && entryId) {
      return { type: 'value', fieldId, entryId };
    }

    return undefined;
  }

  getFirstSelectedValue(): { entryId: string; fieldId: string } | undefined {
    const resolved = this.#api.getResolvedSelection();

    if (resolved.mode === 'cell' && resolved.values.length > 0) {
      const value = resolved.values[0];

      return { entryId: value.entryId, fieldId: value.fieldId };
    }

    return undefined;
  }
}
